import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles, ShieldCheck, Zap, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLeadModal } from '../../lib/leadModalContext';

const BENEFITS = [
  { icon: ShieldCheck, label: 'Practical self-defense' },
  { icon: Zap, label: 'Fitness & strength' },
  { icon: Sparkles, label: 'Confidence & focus' },
  { icon: Users, label: 'Supportive community' },
];

const AdultKarate = () => {
  const { openLeadModal } = useLeadModal();

  return (
    <section
      id="adult-karate"
      className="relative py-16 sm:py-24"
      data-testid="adult-karate-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border border-[color:var(--neon-red)]/35 panel"
          style={{
            boxShadow:
              '0 0 0 1px rgba(255,42,42,0.35), 0 40px 90px rgba(0,0,0,0.55), inset 0 0 60px rgba(255,42,42,0.06)',
          }}
        >
          {/* Ambient red radial glow */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(700px circle at 20% 20%, rgba(255,42,42,0.20), transparent 60%), radial-gradient(600px circle at 90% 90%, rgba(125,249,255,0.10), transparent 55%)',
            }}
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 p-5 sm:p-10 lg:p-14 items-center">
            {/* Photo / visual side */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative aspect-[4/5] w-full max-w-md mx-auto rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_kempo-patch/artifacts/vymvn7p3_651240555_10239509706508164_2923840365154232931_n.jpeg"
                  alt="Adult karate class at Universal Kempo Karate — Colorado Springs"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: 'center 35%' }}
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(to top, rgba(7,8,10,0.65) 0%, rgba(7,8,10,0.10) 45%, transparent 70%)',
                  }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ boxShadow: 'inset 0 0 0 1px rgba(255,42,42,0.35), inset 0 0 32px rgba(255,42,42,0.15)' }}
                />
                <div className="absolute left-4 bottom-3 right-4">
                  <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-[color:var(--neon-cyan)]">
                    Adult Class · Revolution Location
                  </div>
                  <div className="mt-0.5 font-display text-white text-lg tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">
                    Every age. Every level.
                  </div>
                </div>
              </div>
            </div>

            {/* Copy side */}
            <div className="lg:col-span-7 order-1 lg:order-2 text-left">
              <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
                Adults · Beginners Welcome
              </span>

              <h2
                className="mt-3 font-display font-black tracking-tight text-[2rem] leading-[1.05] sm:text-5xl lg:text-6xl sm:leading-[1.02] hero-title-pulse"
                data-testid="adult-karate-title"
              >
                Adult Karate in
                <br />
                Colorado Springs.
              </h2>

              <p className="mt-5 font-display text-white text-xl sm:text-2xl tracking-tight">
                It’s never too late to start.
              </p>

              <p className="mt-4 text-white/75 text-base sm:text-lg leading-relaxed max-w-2xl">
                Our adult karate classes are designed for beginners and experienced martial artists
                alike. Learn practical self-defense, improve your fitness, develop confidence, and
                challenge yourself in a supportive training environment.
              </p>

              <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-[color:var(--neon-red)]/45 bg-[color:var(--neon-red)]/10 px-3 py-2">
                <Sparkles className="h-4 w-4 text-[color:var(--neon-red)]" />
                <span className="font-display uppercase tracking-[0.14em] text-xs text-white">
                  No experience necessary. Beginners are welcome.
                </span>
              </div>

              {/* Benefit chips */}
              <div className="mt-6 flex flex-wrap gap-2">
                {BENEFITS.map((b) => (
                  <span
                    key={b.label}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/12 bg-white/[0.04] text-xs text-white/85 font-mono-ui uppercase tracking-[0.14em]"
                  >
                    <b.icon className="h-3.5 w-3.5 text-[color:var(--neon-red)]" />
                    {b.label}
                  </span>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
                <Button
                  onClick={() => openLeadModal('adult-karate-section', { program: 'Adult Martial Arts' })}
                  className="w-full sm:w-auto bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] rounded-xl px-6 py-6 text-sm min-h-[52px] glow-red-md chat-orb-pulse"
                  data-testid="adult-karate-book-trial-button"
                >
                  <Sparkles className="h-4 w-4 mr-2" /> Book Your Free Trial Class
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                <a
                  href="tel:+17198960593"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-display uppercase tracking-[0.14em] text-xs text-white border border-white/15 hover:bg-white/5 rounded-xl px-5 py-4 min-h-[48px]"
                  data-testid="adult-karate-call-link"
                >
                  Or Call (719) 896-0593
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AdultKarate;
